import { isOriginValid } from '@lib/util/message-event'
import { usePostRobot } from '@sdk/utils/postrobot'
import type { on } from 'post-robot'
import type pr from 'post-robot'
import { PitcherEventName, type UpdateLocationPitcherEvent } from '@sdk/interfaces'
import { UI_API_METHOD_TYPES } from '@sdk/api/modules/ui/types.ui'
import { getTopPitcherWindow } from '@sdk/utils/window'

type CrossDomainWindowType = Parameters<typeof pr.send>[0]

const NO_ACK_ERROR_MESSAGE = 'No ack for postMessage'
const WINDOWS_CLOSED_ERROR_MESSAGE = 'Window closed for'
const NO_HANDLER_ERROR_MESSAGE = 'No handler found for post message:'

export default function (executeLocationChange: (body: UpdateLocationPitcherEvent) => Promise<void>, isRoot: boolean) {
  let subscribers: MessageEventSource[] = []

  const subscribe = (e: MessageEvent) => {
    const source = e.source

    if (!source) throw new Error(`[sdk.ts] subscribe: source is undefined`)

    !subscribers.includes(source) && subscribers.push(source)
  }

  let broadcastEventListener: null | ReturnType<typeof on> = null
  let updateLocationListener: null | ReturnType<typeof on> = null

  const sendToRoot = async (body: UpdateLocationPitcherEvent): Promise<any> => {
    const pr = await usePostRobot()

    return pr.send(getTopPitcherWindow(), UI_API_METHOD_TYPES.UI_UPDATE_LOCATION, body)
  }

  const initListeners = async () => {
    const pr = await usePostRobot()

    if (isRoot) {
      broadcastEventListener = pr.on(UI_API_METHOD_TYPES.UI_UPDATE_LOCATION, async (e) => {
        if (!isOriginValid(e as MessageEvent)) return

        subscribe(e as MessageEvent)

        const pr = await usePostRobot()
        subscribers = subscribers.filter((s: any) => !s.closed)

        subscribers.forEach((subscriber) => {
          pr.send(subscriber as CrossDomainWindowType, PitcherEventName.UPDATE_LOCATION, e.data).catch((e) => {
            const errorMessages = [WINDOWS_CLOSED_ERROR_MESSAGE, NO_ACK_ERROR_MESSAGE, NO_HANDLER_ERROR_MESSAGE]
            if (errorMessages.some((message) => e.message.startsWith(message))) {
              return
            }

            throw e
          })
        })
      })
    }

    updateLocationListener = pr.on(PitcherEventName.UPDATE_LOCATION, async (e) => {
      if (!isOriginValid(e as MessageEvent)) return

      if (isRoot && window.top === window && window.parent?.__canvasPreviewIframeActive === true) {
        console.info('ignoring location updates for canvas preview mode')
        return
      }

      await executeLocationChange(e.data as UpdateLocationPitcherEvent)
    })
  }

  const removeListeners = async () => {
    broadcastEventListener?.cancel()
    updateLocationListener?.cancel()
  }

  return {
    subscribers,
    initListeners,
    removeListeners,
    sendToRoot,
  }
}
